/**
 * Messfühler: wächst die Seite? (02.10.2026, pre.3606)
 *
 * Journal 02.10.: Den ganzen Tag 1.500–2.300 Knoten im Dokument. Nach 31 Minuten verdeckter Seite mit
 * laufender Tagesaufnahme (16:38–17:10) waren es 44.413, davon nur ~670 im Chat — gut 42.000 Knoten
 * irgendwo anders. Das Scrollen ruckelte danach eine Stunde lang (Bildzeit 27–37 ms statt 16), bis das
 * stille Neuladen um 18:40 sie wegräumte. WO sie hingen, sagte keine Zeile: `[scroll-t]` zählt nur
 * gesamt und im Chat.
 *
 * Dieser Fühler sagt es beim nächsten Mal: Steigt die Zahl über {@link SCHWELLE_KNOTEN}, geht höchstens
 * einmal je Stunde EINE Zeile ins Journal — mit den drei Elementen, die die meisten direkten Kinder
 * haben. Wer wiederholt anhängt und nie entfernt, hängt an EINEN Eltern-Knoten an; der steht dann vorn.
 *
 * Geprüft wird jede Minute (auch verdeckt, solange die Seite rechnen darf) und beim Wechsel der
 * Sichtbarkeit — `sichtbar=` und `anlass=` sagen, ob es im Hintergrund oder nach der Rückkehr geschah.
 *
 * ⚠️ Nur Selektoren (Tag, id, zwei Klassen) und Zahlen — nie Text, nie Attribute mit Inhalt.
 */
import { meldeSpur } from "./mikro-spur.js";

/** Ab hier ist die Seite verdächtig groß. Normal: 1.500–2.300 (Journal seit 02.10.). */
export const SCHWELLE_KNOTEN = 6_000;
/** Höchstens eine Zeile je Stunde. */
export const MELDE_ABSTAND_MS = 60 * 60_000;
/** So oft wird gezählt. Das Zählen allein kostet bei 2.000 Knoten nichts Messbares. */
export const PRUEF_TAKT_MS = 60_000;
/** So viele Eltern stehen in der Zeile. */
const GROESSTE_ANZAHL = 3;

export type Anlass = "takt" | "verdeckt" | "zurueck";

interface Groesster {
  selektor: string;
  anzahl: number;
}

/** Nur Zeichen, die in Code-Namen vorkommen — was anders aussieht, ist kein Selektor. */
function sauber(s: string): string {
  return s.replace(/[^A-Za-z0-9_-]/g, "").slice(0, 40);
}

/** `div#id.klasse1.klasse2` — höchstens zwei Klassen. */
export function selektorVon(el: Element): string {
  let s = el.tagName.toLowerCase();
  const id = sauber(el.id);
  if (id) s += `#${id}`;
  const klassen = Array.from(el.classList).map(sauber).filter(Boolean).slice(0, 2);
  for (const k of klassen) s += `.${k}`;
  return s;
}

/** Mit dem Eltern-Element davor (`body>div.toast-host`), damit gleich benannte Listen unterscheidbar sind. */
function pfadVon(el: Element): string {
  const eltern = el.parentElement;
  return eltern ? `${selektorVon(eltern)}>${selektorVon(el)}` : selektorVon(el);
}

/** Die `n` Elemente mit den meisten direkten Kind-Elementen, größtes zuerst. */
export function groessteEltern(doc: Document, n = GROESSTE_ANZAHL): Groesster[] {
  const top: Array<{ el: Element; anzahl: number }> = [];
  for (const el of Array.from(doc.getElementsByTagName("*"))) {
    const anzahl = el.childElementCount;
    if (anzahl === 0) continue;
    if (top.length === n && anzahl <= (top[n - 1] as { anzahl: number }).anzahl) continue;
    top.push({ el, anzahl });
    top.sort((a, b) => b.anzahl - a.anzahl);
    if (top.length > n) top.pop();
  }
  return top.map((t) => ({ selektor: pfadVon(t.el), anzahl: t.anzahl }));
}

/** Die Zeile ohne das Präfix (das setzt `meldeSpur`). Reine Rechnung. */
/**
 * Wo liegen die Knoten? (pre.3612) Die Zeile vom 02.10., 22:04 (`knoten=44941`) nannte als größte Eltern nur
 * 148, 90 und 83 Kinder — die 43.000 zusätzlichen Knoten stecken also nicht in EINER langen Liste, sondern
 * verteilt in einem tiefen Ast. Der Schwerpunkt-Pfad folgt von <body> aus jeweils dem Kind mit den meisten
 * Nachkommen, solange es mindestens die Hälfte trägt — und nennt je Schritt Selektor und Nachkommen. Eine Runde
 * über alle Elemente (rückwärts, jedes zählt sich zu seinem Elternteil), nur beim Melden.
 */
export function schwerpunktPfad(doc: Document, schritte = 6): string {
  const alle = Array.from(doc.getElementsByTagName("*"));
  const groesse = new Map<Element, number>();
  for (let i = alle.length - 1; i >= 0; i--) {
    const el = alle[i];
    const n = (groesse.get(el) ?? 0) + 1;
    groesse.set(el, n);
    const e = el.parentElement;
    if (e) groesse.set(e, (groesse.get(e) ?? 0) + n);
  }
  const teile: string[] = [];
  let hier: Element | null = doc.body;
  while (hier && teile.length < schritte) {
    const gesamt = groesse.get(hier) ?? 0;
    teile.push(`${selektorVon(hier)}:${gesamt}`);
    let bestes: Element | null = null;
    for (const k of Array.from(hier.children)) if (!bestes || (groesse.get(k) ?? 0) > (groesse.get(bestes) ?? 0)) bestes = k;
    if (!bestes || (groesse.get(bestes) ?? 0) * 2 < gesamt) break;
    hier = bestes;
  }
  return teile.join(">") || "-";
}

export function domWachstumZeile(knoten: number, groesste: readonly Groesster[], sichtbar: boolean, anlass: Anlass, pfad = "-"): string {
  const liste = groesste.map((g) => `${g.selektor}:${g.anzahl}`).join(",") || "-";
  // Die Schlüssel sind Protokoll (ASCII, wie `[scroll-t]`), kein Text auf dem Schirm.
  return [`knoten=${knoten}`, `groesste=${liste}`, `sichtbar=${sichtbar ? "ja" : "nein"}`, `anlass=${anlass}`, `pfad=${pfad}`].join(" ");
}

interface DomWachstum {
  /** Zählen und, wenn nötig und erlaubt, melden. `true`, wenn eine Zeile hinausging. */
  pruefe: (anlass: Anlass) => boolean;
}

export function erzeugeDomWachstum(d: {
  doc: Document;
  melde: (zeile: string) => void;
  jetzt?: () => number;
}): DomWachstum {
  const jetzt = d.jetzt ?? (() => Date.now());
  let zuletzt: number | null = null;
  return {
    pruefe(anlass) {
      try {
        const knoten = d.doc.getElementsByTagName("*").length;
        if (knoten <= SCHWELLE_KNOTEN) return false;
        const t = jetzt();
        if (zuletzt !== null && t - zuletzt < MELDE_ABSTAND_MS) return false;
        zuletzt = t;
        d.melde(domWachstumZeile(knoten, groessteEltern(d.doc), d.doc.visibilityState === "visible", anlass, schwerpunktPfad(d.doc)));
        return true;
      } catch {
        // Ein Messfühler darf nie etwas anderes stören.
        return false;
      }
    },
  };
}

let laeuft = false;

/** Den Fühler einschalten (einmal). */
export function starteDomWachstum(): void {
  if (laeuft || typeof document === "undefined") return;
  laeuft = true;
  const f = erzeugeDomWachstum({ doc: document, melde: (z) => meldeSpur("dom-wachstum", z) });
  window.setInterval(() => f.pruefe("takt"), PRUEF_TAKT_MS);
  // Beim Verdecken: was bis dahin wuchs. Beim Zurückkommen: was im Hintergrund dazukam — synchron,
  // bevor die Rückkehr-Abfragen etwas zeichnen.
  document.addEventListener("visibilitychange", () => {
    f.pruefe(document.visibilityState === "visible" ? "zurueck" : "verdeckt");
  });
}
